import React, { useEffect, useMemo, useState } from "react";
import { api, fmtINR, waLink } from "@/lib/api";
import { useAuth } from "@/auth";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ContainerDetailDialog from "@/components/ContainerDetailDialog";
import { Search, ChevronRight, Share2 } from "lucide-react";

const ALL = "__all__";

export default function CatalogPage() {
  const { user } = useAuth();
  const [containers, setContainers] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState(ALL);
  const [model, setModel] = useState(ALL);
  const [cls, setCls] = useState(ALL);
  const [floor, setFloor] = useState(ALL);
  const [detail, setDetail] = useState(null);
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    api.get("/containers").then(r => setContainers(r.data));
    api.get("/gallery-photos").then(r => setPhotos(r.data));
  }, []);

  const uniq = key => [...new Set(containers.map(c => c[key]).filter(Boolean))].sort();

  const filtered = useMemo(() => {
    const terms = q.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
    return containers.filter(c => {
      const searchable = [
        c.name, c.category, c.model, c.class_code, c.external_dimensions,
        c.internal_dimensions, c.flooring_remarks, c.interior, c.exterior,
        c.rear_door, c.insulation, c.product_name, c.doc_code
      ].filter(v => v != null).join(" ").toLocaleLowerCase().replace(/[×x]/g, "x");
      if (terms.some(term => !searchable.includes(term.replace(/[×x]/g, "x")))) return false;
      if (cat !== ALL && c.category !== cat) return false;
      if (model !== ALL && c.model !== model) return false;
      if (cls !== ALL && c.class_code !== cls) return false;
      if (floor !== ALL && c.flooring_remarks !== floor) return false;
      return true;
    });
  }, [containers, q, cat, model, cls, floor]);

  const clear = () => { setQ(""); setCat(ALL); setModel(ALL); setCls(ALL); setFloor(ALL); };

  const canShare = user.role !== "supervisor" && (user.permissions?.share_whatsapp ?? true);

  const FilterSelect = ({ label, value, onChange, options, tid }) => (
    <div>
      <label className="text-eyebrow block mb-1.5">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={tid} className="bg-white rounded-xl h-11"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>All</SelectItem>
          {options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div>
      <h1 className="text-2xl font-semibold text-slate-900">Catalog</h1>
      <p className="text-sm text-slate-500 mt-1">Choose the options below, then pick a container from the list.</p>

      <div className="fc-card p-4 mt-4 space-y-3.5">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <VehicleSharePanel />
          <Input data-testid="catalog-search-input" value={q} onChange={e => {
            if (!q.trim() && e.target.value.trim()) {
              setCat(ALL); setModel(ALL); setCls(ALL); setFloor(ALL);
            }
            setQ(e.target.value);
          }}
            placeholder="Search any container…" className="pl-10 h-11 rounded-xl bg-white" />
        </div>
        <FilterSelect label="Category" value={cat} onChange={setCat} options={uniq("category")} tid="catalog-category-filter" />
        <FilterSelect label="Container Type / Model" value={model} onChange={setModel} options={uniq("model")} tid="catalog-model-filter" />
        <FilterSelect label="Class Code" value={cls} onChange={setCls} options={uniq("class_code")} tid="catalog-class-filter" />
        <FilterSelect label="Flooring / Remarks" value={floor} onChange={setFloor} options={uniq("flooring_remarks")} tid="catalog-flooring-filter" />
        <Button variant="outline" className="w-full rounded-xl h-11" onClick={clear} data-testid="catalog-clear-filters-button">Clear filters</Button>
      </div>

      <div className="text-eyebrow mt-6 mb-3" data-testid="container-count-label">{filtered.length} Container{filtered.length !== 1 ? "s" : ""}</div>

      <div className="space-y-3" data-testid="product-catalog-grid">
        {filtered.map(c => (
          <button key={c.id} onClick={() => setDetail(c)} data-testid={`container-row-${c.id}`}
            className="w-full text-left bg-white border border-slate-200 rounded-2xl p-4 flex items-center justify-between gap-3 hover:shadow-md hover:border-blue-200 transition-all duration-200">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-900 truncate">{c.name}</div>
              <div className="text-xs text-slate-500 mt-1 uppercase tracking-wide">{c.category}{c.class_code ? ` · ${c.class_code}` : ""}</div>
              {c.flooring_remarks && <div className="text-xs text-slate-500 mt-0.5">{c.flooring_remarks}</div>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {c.final_price != null && (
                <div className="text-right">
                  <div className="text-sm font-semibold text-blue-600 tabular-nums">{fmtINR(c.final_price)}</div>
                  <div className="text-[10px] text-slate-400">final price</div>
                </div>
              )}
              <ChevronRight className="w-4 h-4 text-slate-400" />
            </div>
          </button>
        ))}
        {filtered.length === 0 && <div className="text-center py-10 text-slate-500 text-sm">No containers match these filters.</div>}
      </div>

      <ContainerDetailDialog container={detail} photos={photos} onClose={() => setDetail(null)} canShare={canShare} />
    </div>
  );
}


function VehicleSharePanel(){
  const [open,setOpen]=React.useState(false);
  const [vehicles,setVehicles]=React.useState([]);
  const [q,setQ]=React.useState("");
  React.useEffect(()=>{api.get("/vehicles").then(r=>setVehicles(Array.isArray(r.data)?r.data:[])).catch(()=>{});},[]);
  const matches=q.trim()?vehicles.filter(v=>String(v.name||v.model||"").toLowerCase().includes(q.trim().toLowerCase())).slice(0,8):[];
  const textFor=v=>{
    const lines=["FreshCare Vehicle: "+(v.name||v.model||"Vehicle")];
    [["Manufacturer",v.manufacturer||v.make],["Model",v.model],["Type",v.category||v.type],["Payload",v.payload||v.payload_capacity],["GVW",v.gvw],["Deck / body length",v.deck_length||v.body_length],["Wheelbase",v.wheelbase]].forEach(row=>{if(row[1]&&String(row[1])!==String(v.name||""))lines.push(row[0]+": "+row[1]);});
    return lines.join("\n");
  };
  const share=async v=>{
    const text=textFor(v);
    try{if(navigator.share){await navigator.share({title:v.name||"FreshCare Vehicle",text:text});return;}}catch(e){if(e&&e.name==="AbortError")return;}
    try{if(navigator.clipboard)await navigator.clipboard.writeText(text);}catch(e){}
    window.open("https://wa.me/?text="+encodeURIComponent(text),"_blank");
  };
  const withPhotos=v=>{const name=v.name||v.model||"";window.location.href="/gallery?vehicle="+encodeURIComponent(name)+"&vehicle_details=1";};
  return <section className="mb-4 rounded-2xl border border-blue-100 bg-blue-50/40 p-4"><button type="button" onClick={()=>setOpen(v=>!v)} className="flex w-full items-center justify-between gap-3 text-left"><div><div className="text-sm font-semibold text-slate-900">Share vehicle information</div><div className="mt-1 text-xs text-slate-500">Send vehicle master data alone, or choose matching gallery pictures to send with it.</div></div><span className="rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-blue-700">{open?"Close":"Open"}</span></button>{open&&<div className="mt-4"><input type="search" value={q} onChange={e=>setQ(e.target.value)} placeholder="Search Vehicle Master…" className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none focus:border-blue-400"/>{!!q.trim()&&<div className="mt-2 space-y-2">{matches.map(v=><div key={v.id||v.name} className="rounded-xl border border-slate-200 bg-white p-3 sm:flex sm:items-center sm:justify-between sm:gap-3"><div className="min-w-0"><div className="truncate text-sm font-semibold text-slate-900">{v.name||v.model||"Vehicle"}</div><div className="mt-0.5 truncate text-xs text-slate-500">{[v.manufacturer||v.make,v.category||v.type,v.payload||v.payload_capacity].filter(Boolean).join(" · ")}</div></div><div className="mt-2 flex shrink-0 gap-2 sm:mt-0"><button type="button" onClick={()=>share(v)} className="rounded-lg border border-blue-200 bg-white px-3 py-2 text-xs font-semibold text-blue-700">Share data</button><button type="button" onClick={()=>withPhotos(v)} className="rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white">Data + pictures</button></div></div>)}{!matches.length&&<div className="rounded-xl bg-white p-3 text-xs text-slate-500">No matching vehicle in the master list.</div>}</div>}</div>}</section>;
}
